/**
 * Blog article conversion helpers:
 * - Rewrite stale prices ($36.99 / $39.99 / $40) to live PDP price
 * - Add UTMs to product links
 * - Inject mid-article CTA on long posts only (default >=600 words)
 * - Remove baked-in / mid CTAs on short posts (footer CTA stays)
 * - Remove duplicate end-of-article product buttons when theme footer CTA exists
 * - Show sticky shop bar after scroll
 */
(function () {
  'use strict';

  var root = document.querySelector('.article-template');
  if (!root) return;

  var content = root.querySelector('.article-template__content');
  var midTemplate = document.getElementById('ArticleMidCtaTemplate');
  var sticky = document.getElementById('ArticleStickyCta');
  var footerCta = root.querySelector('[data-cta-placement="footer"]');
  var livePrice = (root.getAttribute('data-product-price') || 'US$49.99')
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;[^&]*&gt;/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  // Keep currency-aware prefixes from Liquid (CA$73, US$49.99, etc.)
  if (!/^(?:[A-Z]{1,2}\$|£|€|\$)?\d/.test(livePrice)) livePrice = 'US$49.99';
  var articleHandle = (root.getAttribute('data-article-handle') || 'article').split('/').pop();

  // Mid CTA length gate - must match Liquid mid_cta_min_words (and ~ reading-stats WPM).
  var MID_CTA_MIN_WORDS = parseInt(root.getAttribute('data-mid-cta-min-words') || '600', 10) || 600;

  function countWords(text) {
    return (text || '').trim().split(/\s+/).filter(Boolean).length;
  }

  function isLongEnoughForMidCta() {
    var eligibleAttr = (root.getAttribute('data-mid-cta-eligible') || '').toLowerCase();
    if (eligibleAttr === 'true') return true;
    if (eligibleAttr === 'false') return false;

    var attrWords = parseInt(root.getAttribute('data-article-words') || '', 10);
    if (!isNaN(attrWords) && attrWords > 0) return attrWords >= MID_CTA_MIN_WORDS;

    if (!content) return false;
    return countWords(content.innerText || content.textContent || '') >= MID_CTA_MIN_WORDS;
  }

  function withUtm(href, contentLabel) {
    try {
      var url = new URL(href, window.location.origin);
      if (!/\/products\/bleshark-nano/i.test(url.pathname)) return href;
      if (!url.searchParams.get('utm_source')) url.searchParams.set('utm_source', 'blog');
      if (!url.searchParams.get('utm_medium')) url.searchParams.set('utm_medium', 'cta');
      if (!url.searchParams.get('utm_campaign')) url.searchParams.set('utm_campaign', articleHandle);
      if (contentLabel && !url.searchParams.get('utm_content')) {
        url.searchParams.set('utm_content', contentLabel);
      }
      return url.pathname + url.search + url.hash;
    } catch (e) {
      return href;
    }
  }

  function rewritePrices(scope) {
    if (!scope) return;
    var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    var priceRe = /\$\s?(?:36\.99|39\.99|40(?:\.00)?)\+?/g;
    nodes.forEach(function (node) {
      if (!node.nodeValue || !priceRe.test(node.nodeValue)) return;
      priceRe.lastIndex = 0;
      node.nodeValue = node.nodeValue.replace(priceRe, livePrice);
    });

    scope.querySelectorAll('a, span, strong, em, p, li, h2, h3').forEach(function (el) {
      if (!el.childElementCount && el.textContent && /\$\s?(?:36\.99|39\.99|40)/.test(el.textContent)) {
        priceRe.lastIndex = 0;
        el.textContent = el.textContent.replace(priceRe, livePrice);
      }
    });
  }

  function enhanceProductLinks(scope, contentLabel) {
    if (!scope) return;
    scope.querySelectorAll('a[href*="bleshark-nano"], a[href*="/products/"]').forEach(function (a) {
      var href = a.getAttribute('href') || '';
      if (!/bleshark-nano/i.test(href) && !/\/products\/bleshark-nano/i.test(href)) return;
      a.setAttribute('href', withUtm(href, contentLabel || 'inline'));
      if (/\$\s?(?:36\.99|39\.99|40)/.test(a.textContent || '')) {
        a.textContent = (a.textContent || '').replace(/\$\s?(?:36\.99|39\.99|40(?:\.00)?)\+?/g, livePrice);
      }
    });
  }

  function isProductCtaLink(a) {
    if (!a || !a.getAttribute) return false;
    var href = a.getAttribute('href') || '';
    if (!/bleshark-nano/i.test(href) && !/\/products\/bleshark-nano/i.test(href)) return false;
    var text = (a.textContent || '').toLowerCase();
    return (
      /get (the )?bleshark|shop nano|buy nano|explore bleshark|\$\d+/.test(text) ||
      a.classList.contains('button') ||
      (a.closest('p') && a.closest('p').querySelectorAll('a').length === 1)
    );
  }

  /**
   * Articles already end with a "Get the BLEShark Nano" button.
   * Theme footer CTA replaces that - remove trailing body CTAs to avoid doubles.
   */
  function removeDuplicateEndCtas() {
    if (!content || !footerCta) return;

    var children = Array.prototype.slice.call(content.children);
    if (!children.length) return;

    // Walk backwards from the end; strip consecutive product-CTA blocks
    for (var i = children.length - 1; i >= 0; i--) {
      var el = children[i];
      if (!el || el.hasAttribute('data-article-inline-cta')) break;

      var tag = (el.tagName || '').toLowerCase();
      // Stop when we hit real content (not a lone CTA paragraph)
      if (tag === 'h2' || tag === 'h3' || tag === 'h4' || tag === 'pre' || tag === 'ul' || tag === 'ol' || tag === 'table') {
        break;
      }

      var links = el.querySelectorAll ? el.querySelectorAll('a') : [];
      var onlyCta =
        links.length > 0 &&
        Array.prototype.every.call(links, isProductCtaLink) &&
        (el.textContent || '').replace(/\s+/g, ' ').trim().length < 120;

      var isItalicLegalOnly =
        tag === 'p' &&
        el.querySelector('i, em') &&
        !el.querySelector('a') &&
        (el.textContent || '').length < 400;

      if (onlyCta) {
        el.setAttribute('data-theme-cta-deduped', '1');
        el.style.display = 'none';
        continue;
      }

      // Keep legal disclaimer italic line; stop after stripping CTAs above it
      if (isItalicLegalOnly) {
        continue;
      }

      // Non-CTA content - stop stripping
      break;
    }
  }

  function isSkipHeading(h) {
    var t = (h.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!t) return true;
    if (/^table of contents$|^contents$|^toc$|^overview$|^in this (article|post|guide)/.test(t)) return true;
    if (t.length < 3) return true;
    return false;
  }

  function injectMidCta() {
    if (!content) return;
    // Drop any baked-in / stale mid CTAs so theme template copy always wins.
    // On short posts we stop here (no reinject) - footer/sticky CTAs remain.
    content.querySelectorAll('[data-article-inline-cta]').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });
    if (!isLongEnoughForMidCta()) return;
    if (!midTemplate) return;

    var headings = Array.prototype.slice.call(content.querySelectorAll('h2'));
    if (!headings.length) headings = Array.prototype.slice.call(content.querySelectorAll('h3'));

    // Prefer after 2nd real content heading (skip TOC); else after 1st real; else ~40% depth
    var contentHeadings = headings.filter(function (h) {
      return !isSkipHeading(h);
    });

    var insertAfter = null;
    if (contentHeadings.length >= 2) {
      // After first full section (past intro + first body H2)
      insertAfter = contentHeadings[0];
      // Walk to end of that section: last element before next h2
      var nextH = contentHeadings[1];
      var node = insertAfter.nextElementSibling;
      var lastInSection = insertAfter;
      while (node && node !== nextH) {
        if (!node.hasAttribute || !node.hasAttribute('data-article-inline-cta')) {
          lastInSection = node;
        }
        node = node.nextElementSibling;
      }
      insertAfter = lastInSection;
    } else if (contentHeadings.length === 1) {
      // After first section body (~ a few paragraphs)
      insertAfter = contentHeadings[0];
      var n = 0;
      var cur = insertAfter.nextElementSibling;
      while (cur && n < 3) {
        if ((cur.tagName || '').toLowerCase() === 'p') {
          insertAfter = cur;
          n++;
        } else if (/^H[1-4]$/.test(cur.tagName || '')) {
          break;
        } else {
          insertAfter = cur;
        }
        cur = cur.nextElementSibling;
      }
    }

    var ctaNode = midTemplate.content
      ? midTemplate.content.firstElementChild.cloneNode(true)
      : midTemplate.firstElementChild
        ? midTemplate.firstElementChild.cloneNode(true)
        : null;
    if (!ctaNode) return;

    if (insertAfter && insertAfter.parentNode) {
      if (insertAfter.nextSibling) insertAfter.parentNode.insertBefore(ctaNode, insertAfter.nextSibling);
      else insertAfter.parentNode.appendChild(ctaNode);
    } else {
      var children = Array.prototype.slice.call(content.children);
      var idx = Math.max(2, Math.floor(children.length * 0.4));
      if (children[idx]) content.insertBefore(ctaNode, children[idx]);
      else content.appendChild(ctaNode);
    }

    enhanceProductLinks(ctaNode, 'mid');
  }

  function setupSticky() {
    if (!sticky) return;

    // Keep the bar in the DOM and animate opacity/transform only.
    // Toggling the HTML `hidden` attribute (display:none) mid-transition causes flicker.
    // Mount class reserves body padding once so scrollHeight does not thrash at the threshold.
    var shown = false;
    var showRaf = 0;
    var hideTimer = 0;
    var SHOW_PROGRESS = 0.34;
    var HIDE_PROGRESS = 0.28;
    var SHOW_Y = 420;
    var HIDE_Y = 320;
    var TRANSITION_MS = 240;

    sticky.setAttribute('aria-hidden', 'true');
    var spaceReserved = false;

    function clearPending() {
      if (showRaf) {
        cancelAnimationFrame(showRaf);
        showRaf = 0;
      }
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = 0;
      }
    }

    function reveal() {
      clearPending();
      // Ensure starting (off-screen) styles paint before .is-visible, so the transition runs.
      sticky.hidden = false;
      sticky.classList.remove('is-visible');
      // Reserve body space once on first reveal so later hide/show does not
      // change scrollHeight (that feedback loop caused threshold flicker).
      if (!spaceReserved) {
        document.documentElement.classList.add('article-sticky-cta-mounted');
        spaceReserved = true;
      }
      // Force layout so the browser commits the pre-visible transform/opacity.
      void sticky.offsetWidth;
      showRaf = requestAnimationFrame(function () {
        showRaf = requestAnimationFrame(function () {
          showRaf = 0;
          sticky.classList.add('is-visible');
          sticky.setAttribute('aria-hidden', 'false');
        });
      });
    }

    function conceal() {
      clearPending();
      sticky.classList.remove('is-visible');
      sticky.setAttribute('aria-hidden', 'true');
      // Defer `hidden` until after the exit transition finishes (do not cut it off).
      hideTimer = setTimeout(function () {
        hideTimer = 0;
        if (!shown) sticky.hidden = true;
      }, TRANSITION_MS);
    }

    function onScroll() {
      var y = window.scrollY || window.pageYOffset;
      var docH = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      var viewH = window.innerHeight || 1;
      var progress = y / Math.max(1, docH - viewH);

      var nearFooter = false;
      if (footerCta) {
        var rect = footerCta.getBoundingClientRect();
        nearFooter = rect.top < viewH * 0.9;
      }
      // Also hide near any visible end CTA still in content
      if (!nearFooter && content) {
        var endLinks = content.querySelectorAll('a[href*="bleshark-nano"]');
        if (endLinks.length) {
          var last = endLinks[endLinks.length - 1];
          if (last && last.offsetParent !== null) {
            var lr = last.getBoundingClientRect();
            if (lr.top < viewH * 0.95 && lr.bottom > 0) nearFooter = true;
          }
        }
      }

      // Hysteresis avoids edge thrash when scroll metrics jitter.
      var shouldShow = shown
        ? !nearFooter && progress >= HIDE_PROGRESS && y >= HIDE_Y
        : !nearFooter && progress > SHOW_PROGRESS && y > SHOW_Y;

      if (shouldShow === shown) return;
      shown = shouldShow;
      if (shown) reveal();
      else conceal();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
  }


  function setupCliInstallCopy() {
    var scope = content || root;
    if (!scope) return;
    scope.querySelectorAll('[data-inf-cli] .m-sdk__copy, [data-inf-cli] .inf-cli__copy').forEach(function (btn) {
      if (btn.getAttribute('data-inf-cli-bound') === '1') return;
      btn.setAttribute('data-inf-cli-bound', '1');
      btn.addEventListener('click', function () {
        var text = btn.getAttribute('data-copy') || '';
        var done = function () {
          var prev = btn.textContent;
          btn.textContent = 'Copied';
          btn.classList.add('is-copied');
          setTimeout(function () {
            btn.textContent = prev;
            btn.classList.remove('is-copied');
          }, 1600);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done).catch(done);
        } else {
          try {
            var ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
          } catch (e) {}
          done();
        }
      });
    });
  }

  // Run
  rewritePrices(content);
  enhanceProductLinks(content, 'body');
  enhanceProductLinks(footerCta, 'footer');
  removeDuplicateEndCtas();
  injectMidCta();
  rewritePrices(content);
  setupSticky();
  setupCliInstallCopy();
})();
